import { Link } from 'react-router-dom';
import Button from '@/components/ui/Button';

export interface SessionTimeoutWarningProps {
  /**
   * Whether the warning modal is open.
   */
  isOpen: boolean;
  /**
   * Remaining time until session expires in milliseconds.
   */
  remainingMs: number;
  /**
   * Callback when user clicks "Stay logged in" or interacts with the modal.
   */
  onExtend: () => void;
  /**
   * Callback when user clicks "Log out now".
   */
  onLogout: () => void;
}

/**
 * Format remaining time for display.
 * Returns a human-readable string like "5 minutes" or "less than a minute".
 */
function formatRemainingTime(ms: number): string {
  const minutes = Math.ceil(ms / 60000);

  if (minutes <= 0) {
    return 'less than a minute';
  }

  if (minutes === 1) {
    return '1 minute';
  }

  return `${minutes} minutes`;
}

/**
 * Modal that warns users their session is about to expire due to inactivity.
 * WP-SESSION-3: Activity Tracking & Auto-Logout Warning
 *
 * Features:
 * - Shows remaining time until expiry
 * - Two action buttons: "Stay logged in" and "Log out now"
 * - Link to Settings > Security to adjust timeout preference
 * - Clicking backdrop extends session (counts as user activity)
 * - Proper ARIA attributes for accessibility (alertdialog)
 *
 * @param props - Component props
 */
export default function SessionTimeoutWarning({
  isOpen,
  remainingMs,
  onExtend,
  onLogout,
}: SessionTimeoutWarningProps) {
  if (!isOpen) return null;

  const displayTime = formatRemainingTime(remainingMs);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-desc"
    >
      {/* Backdrop - clicking extends session (user is active) */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onExtend}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        {/* Warning icon */}
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 mb-4">
          <svg
            className="h-6 w-6 text-amber-600"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
        </div>

        <h2
          id="session-timeout-title"
          className="text-lg font-semibold text-gray-900 text-center mb-2"
        >
          Session Expiring Soon
        </h2>

        <p id="session-timeout-desc" className="text-gray-600 text-center mb-6">
          Your session will expire in {displayTime} due to inactivity.
        </p>

        <div className="flex gap-3">
          <Button
            variant="primary"
            onClick={onExtend}
            className="flex-1"
            autoFocus
          >
            Stay logged in
          </Button>
          <Button
            variant="outline"
            onClick={onLogout}
            className="flex-1"
          >
            Log out now
          </Button>
        </div>

        <p className="text-sm text-gray-500 mt-4 text-center">
          <Link
            to="/settings/security"
            className="text-primary-600 hover:text-primary-700 hover:underline"
            onClick={onExtend}
          >
            Adjust timeout in Settings
          </Link>
        </p>
      </div>
    </div>
  );
}
